import { useState, useMemo } from 'react'
import { InsightContentSettingType } from 'components/insightContent/types'
import { InsightData } from 'schemas/action/InsightDataSchema'
import CountType from 'enums/CountType'
import {
  convertToInsightContentSetting,
  addSelectionFilterToWidgetSetting
} from '../InsightModal/utils'
import {
  CrosstabWidgetsType,
  InsightObjective,
  HeatmapSelectionType,
  BarSelectionType
} from '../../types'
import useDecoratedSearchFilterComposingStrategy from './useSearchFilterComposingStrategy'
import useDecoratedWidgetSettingComposingStrategy from './useWidgetSettingComposingStrategy'
import { resolveSelection, isClickingOnTotalColumn } from './utils'

const useSelectedInsights = (widgetSetting: CrosstabWidgetsType) => {
  const countType = CountType.MENTIONS
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selection, setSelection] = useState<InsightObjective | undefined>(undefined)
  const searchFilterComposingStrategy = useDecoratedSearchFilterComposingStrategy(widgetSetting)
  const widgetSettingComposingStrategy = useDecoratedWidgetSettingComposingStrategy(widgetSetting)

  const detailSetting = useMemo<InsightContentSettingType | undefined>(() => {
    if (isModalOpen && selection) {
      return convertToInsightContentSetting(widgetSetting, searchFilterComposingStrategy, selection)
    }
    return undefined
  }, [isModalOpen, widgetSetting, searchFilterComposingStrategy, selection])

  const widgetSettingWithSelection = useMemo<any>(() => {
    if (isModalOpen && selection) {
      return addSelectionFilterToWidgetSetting(
        widgetSetting,
        widgetSettingComposingStrategy,
        selection
      )
    }
    return undefined
  }, [isModalOpen, widgetSetting, widgetSettingComposingStrategy, selection])

  const handleHeatmapClick = (target: HeatmapSelectionType) => {
    const { custom, value, x } = target
    const { rowId, colId, isDisabled } = custom
    if (!rowId || !colId || isDisabled) {
      return
    }

    if (isClickingOnTotalColumn(widgetSetting, x)) {
      return
    }

    const {
      insight,
      pivot,
      topic,
      selectedPivotThemeInfo,
      selectedPivotRowThemeInfo,
      selectedTagName,
      selectedRowTagName,
      selectedDocName,
      selectedRowDocName
    } = resolveSelection(widgetSetting, rowId, colId)

    setSelection({
      value,
      topicId: topic.topicId!,
      selectedPivotThemeInfo,
      selectedPivotRowThemeInfo,
      selectedTagName,
      selectedRowTagName,
      selectedDocName,
      selectedRowDocName,
      insight: insight as InsightData,
      pivot: pivot as InsightData
    })
    setIsModalOpen(true)
  }

  const handleBarClick = (target: BarSelectionType) => {
    const { y: value, custom } = target
    const { rowId, colId } = custom

    const {
      insight,
      pivot,
      topic,
      selectedPivotThemeInfo,
      selectedPivotRowThemeInfo,
      selectedTagName,
      selectedRowTagName,
      selectedDocName,
      selectedRowDocName
    } = resolveSelection(widgetSetting, rowId, colId)

    setSelection({
      value,
      topicId: topic.topicId!,
      selectedPivotThemeInfo,
      selectedPivotRowThemeInfo,
      selectedTagName,
      selectedRowTagName,
      selectedDocName,
      selectedRowDocName,
      insight: insight as InsightData,
      pivot: pivot as InsightData
    })
    setIsModalOpen(true)
  }

  return {
    isModalOpen,
    setIsModalOpen,
    selection,
    countType,
    detailSetting,
    handleHeatmapClick,
    handleBarClick,
    widgetSettingWithSelection
  }
}

export default useSelectedInsights
